import { useRouter } from 'next/router';
import React from 'react';

import type { Locale, TranslateFn, TranslationKey, TranslationValues } from 'lib/i18n/types';

import { useAppContext } from 'lib/contexts/app';
import * as cookies from 'lib/cookies';
import { resolveLocale } from 'lib/i18n/resolveLocale';
import { DEFAULT_LOCALE, translations } from 'lib/i18n/translations';
import { syncDayjsLocale } from 'lib/date/dayjs';

interface I18nProviderProps {
  children: React.ReactNode;
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslateFn;
}

const I18nContext = React.createContext<I18nContextValue | null>(null);

function getLangParam(query: Record<string, string | Array<string> | undefined> | undefined) {
  return query?.lang;
}

function translate(locale: Locale, key: TranslationKey, values?: TranslationValues) {
  const template = translations[locale][key] ?? translations[DEFAULT_LOCALE][key] ?? key;

  if (!values) {
    return template;
  }

  return Object.entries(values).reduce((result, [ name, value ]) => {
    return result.replaceAll(`{${ name }}`, String(value));
  }, template);
}

export function I18nContextProvider({ children }: I18nProviderProps) {
  const router = useRouter();
  const { query, cookies: cookiesString } = useAppContext();

  const [ locale, setLocaleState ] = React.useState<Locale>(() => {
    const initialLocale = resolveLocale(getLangParam(query), cookiesString);
    syncDayjsLocale(initialLocale);
    return initialLocale;
  });

  React.useEffect(() => {
    document.documentElement.lang = locale;
    syncDayjsLocale(locale);
  }, [ locale ]);

  React.useEffect(() => {
    const nextLocale = resolveLocale(router.query.lang);

    setLocaleState((currentLocale) => {
      if (currentLocale === nextLocale) {
        return currentLocale;
      }
      syncDayjsLocale(nextLocale);
      return nextLocale;
    });
    cookies.set(cookies.NAMES.LOCALE, nextLocale);
  }, [ router.query.lang ]);

  const setLocale = React.useCallback((nextLocale: Locale) => {
    syncDayjsLocale(nextLocale);
    setLocaleState(nextLocale);
    cookies.set(cookies.NAMES.LOCALE, nextLocale);
  }, []);

  const t = React.useCallback((key: TranslationKey, values?: TranslationValues) => {
    return translate(locale, key, values);
  }, [ locale ]);

  const value = React.useMemo(() => ({
    locale,
    setLocale,
    t,
  }), [ locale, setLocale, t ]);

  return (
    <I18nContext.Provider value={ value }>
      { children }
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = React.useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within I18nContextProvider');
  }

  return context;
}
